import React, { useEffect, useMemo, useState } from "react";
import { api, formatMXN, formatApiError, imageUrl } from "../lib/api";
import { config } from "../lib/config";
import { tokenStore } from "../lib/http";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import {
  X, Sparkle, DownloadSimple, ArrowsClockwise, WarningCircle, CheckCircle,
  ShareNetwork, WhatsappLogo, FacebookLogo, InstagramLogo, Copy, Info, Image as ImageIcon,
} from "@phosphor-icons/react";
import { AtlasDraftModal } from "./AtlasDraftModal";

/**
 * FlyerModal — Fase 6 (mínima).
 * Plantilla luxury dark/gold cuadrada 1080x1080. La foto principal se conserva
 * intacta (100% determinístico, sin IA). ATLAS solo propone título y CTA.
 * Nada se descarga, guarda ni comparte sin confirmación del asesor.
 */
export function FlyerModal({ open, property, onClose }) {
  const [validating, setValidating] = useState(true);
  const [validation, setValidation] = useState({ ok: false, errors: [] });
  const [titleOverride, setTitleOverride] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewBlob, setPreviewBlob] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState("");
  const [atlasKind, setAtlasKind] = useState(null); // 'flyer_headline'
  const [confirming, setConfirming] = useState(null); // 'whatsapp' | 'facebook' | 'instagram' | 'download' | 'universal'
  const [nativeSharing, setNativeSharing] = useState(false); // evita doble tap durante share
  // Selector de plantilla (V1 — 3 diseños luxury).
  const [template, setTemplate] = useState("photo"); // 'classic' | 'photo' | 'clear' — default 'photo' (más impacto redes)
  const [templatePreviews, setTemplatePreviews] = useState({ classic: "", photo: "", clear: "" });
  const [zoomTemplate, setZoomTemplate] = useState(null); // template en modal "Ver en grande"

  const TEMPLATES = [
    { id: "classic", name: "Clásico dorado",   hint: "Elegante y balanceado. Ideal para propiedades premium." },
    { id: "photo",   name: "Foto protagonista", hint: "Foto grande + overlay. Ideal para Facebook e Instagram." },
    { id: "clear",   name: "Comercial claro",   hint: "Legibilidad máxima. Ideal para lectura rápida en móvil." },
  ];

  const publicUrl = property?.id
    ? `${window.location.origin}/propiedades/${property.id}`
    : "";
  // URL para mensajería / redes: apunta al endpoint OG del backend para que
  // WhatsApp/Facebook muestren la foto real en la vista previa.
  const socialUrl = property?.id
    ? `${window.location.origin}/api/share/property/${property.id}?utm_source=flyer&utm_medium=share`
    : "";
  // URL pública del flyer (HTML con OG) — usada por el botón Facebook para
  // que la vista previa muestre el flyer 1080×1080 completo (según template).
  const flyerShareUrl = property?.id
    ? `${window.location.origin}/api/share/flyer/${property.id}?template=${template}&utm_source=facebook&utm_medium=share`
    : "";
  // Alias para retrocompatibilidad con el botón Facebook.
  const facebookUrl = flyerShareUrl || socialUrl;

  useEffect(() => {
    if (!open || !property?.id) return;
    setValidating(true); setError(""); setPreviewUrl(""); setPreviewBlob(null);
    setTitleOverride("");
    api.post(`/properties/${property.id}/flyer/validate`)
      .then(({ data }) => setValidation(data))
      .catch((err) => setError(formatApiError(err, "No se pudo validar la propiedad.")))
      .finally(() => setValidating(false));
    return () => {
      if (previewUrl) { try { URL.revokeObjectURL(previewUrl); } catch {} }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, property?.id]);

  // Renderiza UN template y devuelve {blob, url}. Con timeout de 30s.
  const renderOne = async (tmpl) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const token = tokenStore.get();
      const resp = await fetch(`${config.backendUrl}/api/properties/${property.id}/flyer`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ title_override: titleOverride, template: tmpl }),
        signal: controller.signal,
      });
      if (!resp.ok) {
        let msg = "No se pudo generar el flyer.";
        try {
          const j = await resp.json();
          if (j?.detail?.message) msg = j.detail.message;
          else if (typeof j?.detail === "string") msg = j.detail;
        } catch {}
        throw new Error(msg);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      return { blob, url };
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const generatePreview = async () => {
    if (!validation.ok) return;
    setRendering(true); setError("");
    // Limpia URLs previas para evitar leaks.
    try { Object.values(templatePreviews).forEach(u => u && URL.revokeObjectURL(u)); } catch {}
    setTemplatePreviews({ classic: "", photo: "", clear: "" });
    setPreviewBlob(null); setPreviewUrl("");
    try {
      // Genera los 3 templates en paralelo. Si alguno falla, seguimos con los demás.
      const results = await Promise.allSettled([
        renderOne("classic"), renderOne("photo"), renderOne("clear"),
      ]);
      const previews = { classic: "", photo: "", clear: "" };
      const blobs = {};
      ["classic", "photo", "clear"].forEach((k, i) => {
        if (results[i].status === "fulfilled") {
          previews[k] = results[i].value.url;
          blobs[k] = results[i].value.blob;
        }
      });
      setTemplatePreviews(previews);
      // Seleccionamos el template elegido (o classic por defecto) como activo.
      const chosen = previews[template] ? template : "classic";
      setTemplate(chosen);
      if (blobs[chosen]) {
        setPreviewBlob(blobs[chosen]);
        setPreviewUrl(previews[chosen]);
      }
      if (!Object.values(previews).some(Boolean)) {
        const firstErr = results.find(r => r.status === "rejected");
        setError(firstErr?.reason?.message || "No se pudo generar el flyer.");
      }
    } catch (err) {
      if (err?.name === "AbortError") {
        setError("La generación tardó demasiado. Verifica tu conexión e inténtalo de nuevo.");
      } else {
        setError(err?.message || "No se pudo generar el flyer.");
      }
    } finally {
      setRendering(false);
    }
  };

  // Cambio de template desde el selector — actualiza el blob/url actual sin re-generar.
  const selectTemplate = (tmpl) => {
    setTemplate(tmpl);
    const url = templatePreviews[tmpl];
    if (url) {
      setPreviewUrl(url);
      // Recuperar el blob desde la URL (fetch local, muy rápido en memoria).
      fetch(url).then(r => r.blob()).then(setPreviewBlob).catch(() => {});
    }
  };

  const doDownload = () => {
    if (!previewBlob) return;
    const link = document.createElement("a");
    link.href = URL.createObjectURL(previewBlob);
    link.download = `flyer-${(property?.title || "propiedad").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40)}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success("Flyer descargado");
    setConfirming(null);
  };

  // Construye un File a partir del blob del flyer para navigator.share({files}).
  const buildFlyerFile = () => {
    if (!previewBlob) return null;
    const safe = (property?.title || "propiedad").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40);
    return new File([previewBlob], `flyer-${safe}.png`, { type: "image/png" });
  };

  // Intenta compartir el flyer + texto vía Web Share API con archivo.
  // - Devuelve "shared" si el share sheet aceptó el archivo.
  // - Devuelve "cancelled" si el usuario canceló (AbortError).
  // - Devuelve "unsupported" si no hay soporte (fallback manual).
  const tryNativeFileShare = async ({ title, text } = {}) => {
    if (nativeSharing) return "busy";
    const file = buildFlyerFile();
    if (!file) return "unsupported";
    if (!navigator.share || !navigator.canShare) return "unsupported";
    let canShareFiles = false;
    try { canShareFiles = navigator.canShare({ files: [file] }); } catch { canShareFiles = false; }
    if (!canShareFiles) return "unsupported";
    setNativeSharing(true);
    try {
      await navigator.share({
        title: title || property?.title || "ROCAFUERTE GO",
        text: text || shareText,
        files: [file],
      });
      return "shared";
    } catch (e) {
      if (e?.name === "AbortError") return "cancelled";
      return "unsupported";
    } finally {
      setNativeSharing(false);
    }
  };

  // Descarga programática del flyer sin cerrar el modal ni pedir confirmación.
  const silentDownload = () => {
    if (!previewBlob) return;
    const link = document.createElement("a");
    link.href = URL.createObjectURL(previewBlob);
    link.download = `flyer-${(property?.title || "propiedad").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40)}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // "Compartir flyer" universal: intenta share nativo con el PNG.
  // Si no hay soporte, cae al fallback (descarga + copiar texto + abrir Facebook).
  const shareFlyerUniversal = async () => {
    if (nativeSharing || !previewBlob) return;
    const result = await tryNativeFileShare();
    if (result === "shared") {
      toast.success("Flyer compartido");
      setConfirming(null);
      return;
    }
    if (result === "cancelled") {
      // El usuario canceló dentro del share sheet: no hacemos nada más.
      return;
    }
    // Fallback: descargar + copiar texto + mostrar guía.
    silentDownload();
    try { await navigator.clipboard.writeText(shareText); } catch {}
    toast.success("Guardamos el flyer y copiamos el texto. Elige la app donde quieres pegarlo.");
    setConfirming(null);
  };

  const shareText = useMemo(() => {
    const parts = [];
    parts.push(titleOverride?.trim() || property?.title || "Nueva propiedad");
    if (property?.price) parts.push(formatMXN(property.price));
    if (property?.city)  parts.push(property.city);
    parts.push("Agenda tu visita por WhatsApp");
    if (socialUrl) parts.push(socialUrl);
    return parts.join(" · ");
  }, [titleOverride, property, socialUrl]);

  const openWhatsApp = async () => {
    if (nativeSharing) return;
    const result = await tryNativeFileShare();
    if (result === "shared") {
      toast.success("Flyer compartido");
      setConfirming(null);
      return;
    }
    if (result === "cancelled") return;
    // Fallback: descargar + abrir wa.me con el texto.
    silentDownload();
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank", "noopener,noreferrer");
    toast.success("Abrimos WhatsApp. Adjunta el flyer descargado.");
    setConfirming(null);
  };
  const openFacebook = async () => {
    if (nativeSharing) return;
    // Facebook web sharer NO acepta archivos por URL. Compartimos la URL del
    // flyer público (que expone og:image = PNG completo) y copiamos el texto.
    try { await navigator.clipboard.writeText(shareText); } catch {}
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(flyerShareUrl || facebookUrl || publicUrl || window.location.origin)}`,
      "_blank", "noopener,noreferrer,width=600,height=520"
    );
    toast.success("Facebook mostrará el flyer como vista previa. También lo guardamos por si prefieres adjuntarlo manualmente.");
    silentDownload();
    setConfirming(null);
  };
  const copyForInstagram = async () => {
    if (nativeSharing) return;
    const result = await tryNativeFileShare();
    if (result === "shared") {
      toast.success("Flyer compartido");
      setConfirming(null);
      return;
    }
    if (result === "cancelled") return;
    // Fallback manual: descargar + copiar texto + guía es-MX.
    silentDownload();
    try { await navigator.clipboard.writeText(shareText); } catch {}
    toast.success("Guardamos el flyer y copiamos el texto. En Instagram selecciona la imagen descargada y pega el texto para publicar.");
    setConfirming(null);
  };
  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Texto copiado");
    } catch { toast.error("No se pudo copiar"); }
  };

  if (!open) return null;

  const mainImage = property?.images?.[0];
  const priceText = property?.price ? formatMXN(property.price) : "—";
  const feats = (property?.features || []).slice(0, 3);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      data-testid="flyer-modal"
    >
      <div className="w-full sm:max-w-3xl bg-[#0F0F0F] border border-[#C8A24A]/30 rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[94vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-[#C8A24A]/15">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-widest text-[#C8A24A]/70 font-semibold inline-flex items-center gap-1.5">
              <ImageIcon size={11} weight="fill" /> ROCAFUERTE GO · Flyer
            </p>
            <h2 className="font-heading text-xl sm:text-2xl mt-1 tracking-tight" data-testid="flyer-modal-title">
              Crear flyer
            </h2>
            <p className="text-xs text-[#C8A24A]/70 mt-1.5 leading-relaxed">
              Plantilla oficial 1080 × 1080. La foto principal se conserva tal cual — no se modifica con IA.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#C8A24A]/60 hover:text-[#C8A24A] p-1 flex-shrink-0"
            data-testid="flyer-close"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {validating && (
            <div className="text-center py-10 text-sm text-[#C8A24A]/70" data-testid="flyer-validating">
              Validando datos...
            </div>
          )}

          {!validating && !validation.ok && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4" data-testid="flyer-blocked">
              <div className="flex items-center gap-2 mb-2">
                <WarningCircle size={18} weight="fill" className="text-red-300" />
                <p className="text-sm font-semibold text-red-200">No podemos crear el flyer todavía</p>
              </div>
              <p className="text-xs text-red-200/80 mb-3">
                Corrige la propiedad antes de continuar. Faltan estos datos:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-xs text-red-100" data-testid="flyer-errors">
                {(validation.errors || []).map((e, i) => (
                  <li key={i} data-testid={`flyer-error-${e.field}`}>{e.message}</li>
                ))}
              </ul>
              {(validation.errors || []).some(e => e.field === "whatsapp") && (
                <a
                  href="/dashboard/cuenta"
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#C8A24A] hover:text-[#E5C275]"
                  data-testid="flyer-goto-profile"
                >
                  Ir a mi perfil para agregar WhatsApp →
                </a>
              )}
              <p className="text-xs text-[#C8A24A]/60 mt-3">
                Vuelve al editor de la propiedad, corrige los datos y reintenta.
              </p>
            </div>
          )}

          {!validating && validation.ok && (
            <>
              {/* Data preview panel */}
              <div className="rounded-xl border border-[#C8A24A]/20 bg-[#151515] p-3 sm:p-4" data-testid="flyer-data-panel">
                <p className="text-xs uppercase tracking-widest text-[#C8A24A]/70 font-semibold mb-2">
                  Datos que aparecerán en el flyer
                </p>
                <div className="grid sm:grid-cols-[140px_1fr] gap-3 items-start">
                  <div className="aspect-square rounded-lg overflow-hidden bg-[#0A0A0A] border border-[#C8A24A]/15" data-testid="flyer-main-photo">
                    {mainImage ? (
                      <img src={imageUrl(mainImage)} alt="Foto principal" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#C8A24A]/40 text-xs">Sin foto</div>
                    )}
                  </div>
                  <ul className="text-xs space-y-1 text-[#C8A24A]/85">
                    <li><b>Operación:</b> {property?.operation === "renta" ? "En renta" : "En venta"}</li>
                    <li><b>Tipo:</b> {property?.property_type}</li>
                    <li><b>Precio:</b> {priceText}</li>
                    <li><b>Ciudad:</b> {property?.city}{property?.state ? `, ${property.state}` : ""}</li>
                    {feats.length > 0 && (
                      <li><b>Características:</b> {feats.join(", ")} <span className="text-[#C8A24A]/50">(máx 3)</span></li>
                    )}
                  </ul>
                </div>
                <div className="mt-3 flex items-start gap-1.5 text-xs text-[#C8A24A]/60 leading-relaxed">
                  <Info size={11} weight="fill" className="flex-shrink-0 mt-0.5" />
                  <span>La foto se recorta al espacio (centro) sin deformación ni retoque por IA. Si el encuadre no te gusta, regresa a editar la propiedad y cambia la foto principal.</span>
                </div>
              </div>

              {/* Editable title */}
              <div>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <label className="text-xs uppercase tracking-widest text-[#C8A24A]/80 font-semibold">
                    Título del flyer (opcional)
                  </label>
                  <button
                    type="button"
                    onClick={() => setAtlasKind("flyer_headline")}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#C8A24A] hover:text-[#E5C275]"
                    data-testid="flyer-atlas-title-btn"
                  >
                    <Sparkle size={11} weight="fill" /> Proponer con ATLAS
                  </button>
                </div>
                <Input
                  value={titleOverride}
                  onChange={(e) => setTitleOverride(e.target.value)}
                  placeholder={property?.title || "Ej. Casa moderna en San Pedro"}
                  maxLength={80}
                  data-testid="flyer-title-input"
                />
                <p className="text-xs text-[#C8A24A]/40 mt-1">
                  Si lo dejas vacío se usa el título de la propiedad.
                </p>
              </div>

              {/* Fixed CTA notice — Fase 6.1 */}
              <div className="rounded-lg border border-[#C8A24A]/25 bg-[#C8A24A]/5 p-3" data-testid="flyer-cta-fixed">
                <p className="text-xs uppercase tracking-widest text-[#C8A24A]/80 font-semibold mb-1">
                  Llamada a la acción del flyer
                </p>
                <p className="text-sm text-[#C8A24A]">AGENDA TU VISITA POR WHATSAPP</p>
                <p className="text-xs text-[#C8A24A]/60 mt-1">
                  Se incluye automáticamente tu nombre y tu WhatsApp registrado. Actualízalos en tu perfil si lo necesitas.
                </p>
              </div>

              {/* Preview / Generate */}
              <div className="rounded-xl border border-[#C8A24A]/25 bg-[#0A0A0A] p-3 sm:p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                  <p className="text-xs uppercase tracking-widest text-[#C8A24A]/80 font-semibold">
                    Vista previa
                  </p>
                  <Button
                    onClick={generatePreview}
                    disabled={rendering}
                    className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-9"
                    data-testid="flyer-generate-btn"
                  >
                    {rendering ? (
                      <><ArrowsClockwise size={14} className="mr-1.5 animate-spin" /> Generando...</>
                    ) : previewUrl ? (
                      <><ArrowsClockwise size={14} className="mr-1.5" /> Regenerar</>
                    ) : (
                      <><Sparkle size={14} weight="fill" className="mr-1.5" /> Generar vista previa</>
                    )}
                  </Button>
                </div>

                {error && !rendering && (
                  <p className="text-sm text-red-300 mb-3" data-testid="flyer-render-error">{error}</p>
                )}

                {!previewUrl && !rendering && !error && (
                  <div className="aspect-square w-full max-w-md mx-auto rounded-lg border border-dashed border-[#C8A24A]/20 flex flex-col items-center justify-center text-center p-6" data-testid="flyer-preview-empty">
                    <ImageIcon size={32} className="text-[#C8A24A]/40 mb-2" />
                    <p className="text-xs text-[#C8A24A]/60">
                      Pulsa <b>Generar vista previa</b> para ver cómo quedará tu flyer.
                    </p>
                  </div>
                )}

                {rendering && (
                  <div className="aspect-square w-full max-w-md mx-auto rounded-lg bg-[#151515] animate-pulse" data-testid="flyer-preview-loading" />
                )}

                {previewUrl && !rendering && (
                  <div className="flex flex-col items-center gap-3">
                    <img
                      src={previewUrl}
                      alt="Vista previa del flyer"
                      className="w-full max-w-md rounded-lg border border-[#C8A24A]/25 shadow-xl"
                      data-testid="flyer-preview-image"
                    />
                    <p className="text-xs text-[#C8A24A]/50 text-center">
                      Nada se ha descargado ni compartido todavía. Confirma la acción para continuar.
                    </p>
                  </div>
                )}
              </div>

              {/* Actions — only when preview exists */}
              {previewUrl && !rendering && (
                <div className="space-y-3" data-testid="flyer-actions">
                  {/* Selector de plantilla — 3 tarjetas con preview real */}
                  <div className="space-y-2">
                    <p className="text-base font-semibold text-[#C8A24A]">Elige el estilo de tu flyer</p>
                    <div className="grid grid-cols-3 gap-2" data-testid="flyer-template-picker">
                      {TEMPLATES.map((t) => {
                        const selected = template === t.id;
                        const url = templatePreviews[t.id];
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => selectTemplate(t.id)}
                            disabled={!url}
                            className={`text-left rounded-xl overflow-hidden border-2 transition ${
                              selected ? "border-[#C8A24A] shadow-lg shadow-[#C8A24A]/20" : "border-[#C8A24A]/25 opacity-90"
                            } ${!url ? "opacity-50" : "hover:border-[#C8A24A]/60"} bg-[#0F0F0F]`}
                            style={{ minHeight: 44 }}
                            data-testid={`flyer-template-${t.id}`}
                          >
                            {url ? (
                              <img src={url} alt={t.name} className="w-full aspect-square object-cover" />
                            ) : (
                              <div className="w-full aspect-square bg-[#1a1a1a] flex items-center justify-center text-xs text-[#C8A24A]/50">Cargando…</div>
                            )}
                            <div className="p-2">
                              <p className="text-sm font-semibold text-[#C8A24A] leading-tight">{t.name}</p>
                              <p className="text-xs text-[#C8A24A]/60 mt-0.5 leading-snug hidden sm:block">{t.hint}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setZoomTemplate(template)}
                        disabled={!previewUrl}
                        className="h-11 text-base min-w-[110px]"
                        data-testid="flyer-zoom-btn"
                      >
                        Ver en grande
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={generatePreview}
                        className="h-11 text-base min-w-[110px]"
                        data-testid="flyer-regenerate-btn"
                      >
                        Cambiar diseño
                      </Button>
                    </div>
                  </div>

                  <Button
                    onClick={shareFlyerUniversal}
                    disabled={nativeSharing}
                    className="w-full bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-11 font-semibold"
                    data-testid="flyer-share-universal"
                  >
                    {nativeSharing ? "Compartiendo…" : "Compartir flyer"}
                  </Button>
                  <div className="grid grid-cols-4 gap-2 sm:gap-3">
                    <ChannelBtn icon={DownloadSimple} label="Descargar"  testId="flyer-download-btn"  onClick={() => setConfirming("download")} />
                    <ChannelBtn icon={WhatsappLogo}   label="WhatsApp"    testId="flyer-share-whatsapp" onClick={() => setConfirming("whatsapp")} />
                    <ChannelBtn icon={FacebookLogo}   label="Facebook"    testId="flyer-share-facebook" onClick={() => setConfirming("facebook")} />
                    <ChannelBtn icon={InstagramLogo}  label="Instagram"   testId="flyer-share-instagram" onClick={() => setConfirming("instagram")} />
                  </div>
                  <div className="flex justify-center">
                    <Button variant="outline" size="sm" onClick={copyText} className="h-9" data-testid="flyer-copy-text-btn">
                      <Copy size={13} className="mr-1.5" /> Copiar texto sugerido
                    </Button>
                  </div>
                </div>
              )}
              {zoomTemplate && templatePreviews[zoomTemplate] && (
                <div
                  className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
                  onClick={() => setZoomTemplate(null)}
                  data-testid="flyer-zoom-modal"
                >
                  <img src={templatePreviews[zoomTemplate]} alt="Flyer ampliado" className="max-w-full max-h-full rounded-lg" />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#C8A24A]/15 flex items-center justify-between gap-3">
          <p className="text-xs text-[#C8A24A]/50 leading-tight">
            Ningún archivo se descarga ni comparte sin tu confirmación.
          </p>
          <Button variant="outline" onClick={onClose} className="h-9" data-testid="flyer-close-btn">
            Cerrar
          </Button>
        </div>
      </div>

      {/* ATLAS nested modal for headline / CTA proposals */}
      {atlasKind && (
        <AtlasDraftModal
          open={!!atlasKind}
          kind={atlasKind}
          propertyData={property || {}}
          onApply={(text) => {
            if (atlasKind === "flyer_headline") setTitleOverride(text);
          }}
          onClose={() => setAtlasKind(null)}
        />
      )}

      {/* Confirmation overlay */}
      {confirming && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="flyer-confirm">
          <div className="w-full max-w-sm bg-[#0F0F0F] border border-[#C8A24A]/40 rounded-2xl p-5 shadow-2xl">
            <p className="text-xs uppercase tracking-widest text-[#C8A24A]/70 font-semibold">Confirmar</p>
            <h3 className="font-heading text-lg mt-1.5" data-testid="flyer-confirm-title">
              {confirming === "download"  && "Descargar flyer"}
              {confirming === "whatsapp"  && "Compartir por WhatsApp"}
              {confirming === "facebook"  && "Compartir en Facebook"}
              {confirming === "instagram" && "Preparar para Instagram"}
            </h3>
            <p className="text-sm text-[#C8A24A]/70 mt-2 leading-relaxed">
              {confirming === "download"  && "Se descargará el flyer como archivo PNG en tu dispositivo."}
              {confirming === "whatsapp"  && "Intentaremos enviar el flyer directamente por WhatsApp. Si tu dispositivo no lo permite, guardamos el archivo y abrimos WhatsApp con el texto listo."}
              {confirming === "facebook"  && "Facebook mostrará el flyer como vista previa en la publicación. También lo guardamos por si prefieres adjuntarlo manualmente."}
              {confirming === "instagram" && "Intentaremos enviar el flyer directamente. Si tu dispositivo no lo permite, guardamos el flyer y copiamos el texto. En Instagram selecciona la imagen descargada y pega el texto para publicar."}
            </p>
            {confirming === "facebook" && (
              <p className="text-xs text-[#C8A24A]/60 mt-2 leading-relaxed border-t border-[#C8A24A]/15 pt-2" data-testid="fb-photo-note-flyer">
                Facebook mostrará el flyer 1080×1080 como preview. Para publicar varias fotos, agrégalas manualmente desde Facebook.
              </p>
            )}
            <div className="mt-4 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirming(null)} className="h-9" data-testid="flyer-confirm-cancel">Cancelar</Button>
              <Button
                onClick={() => {
                  if (confirming === "download") doDownload();
                  else if (confirming === "whatsapp") openWhatsApp();
                  else if (confirming === "facebook") openFacebook();
                  else copyForInstagram();
                }}
                className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-9"
                data-testid="flyer-confirm-ok"
              >
                <CheckCircle size={14} className="mr-1.5" /> Confirmar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChannelBtn({ icon: Icon, label, testId, onClick }) {
  return (
    <button
      onClick={onClick}
      className="group flex flex-col items-center justify-center gap-1.5 rounded-xl border border-[#C8A24A]/30 bg-[#151515] hover:border-[#C8A24A]/60 hover:bg-[#1A1A1A] p-3 sm:p-4 transition-colors"
      data-testid={testId}
    >
      <Icon size={22} weight="duotone" className="text-[#C8A24A] group-hover:scale-110 transition-transform" />
      <span className="text-xs font-semibold text-[#C8A24A]">{label}</span>
    </button>
  );
}
